import { type FC, useCallback, useEffect, useMemo, useState, useTransition } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { type IntlShape, useIntl } from 'react-intl'

import { type PromotionPlacement, PromotionPlacementZod, type PromotionType } from '@onr/schema'

import { Button } from '@/components/atoms/Button'
import { Checkbox } from '@/components/atoms/Checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/atoms/Dialog'
import { FormControl } from '@/components/atoms/Form/FormControl'
import { FormField } from '@/components/atoms/Form/FormField'
import { FormItem } from '@/components/atoms/Form/FormItem'
import { FormLabel } from '@/components/atoms/Form/FormLabel'
import { FormMessage } from '@/components/atoms/Form/FormMessage'
import { Icons } from '@/components/atoms/Icons/Icons'
import { Input } from '@/components/atoms/Input/Input'
import { Switch } from '@/components/atoms/Switch/Switch'
import { FileUpload } from '@/components/molecules/FileUpload/FileUpload'

import { PromotionFormProvider, type PromotionFormProviderProps } from './PromotionFormProvider'
import { type PromotionFormData } from './PromotionFormZod'

export interface PromotionFormProps extends PromotionFormProviderProps {
  storagePath: string
  enabledPlacements?: Partial<Record<PromotionPlacement, boolean>>
  metadata?: Record<string, string>
  onDelete?: () => void | Promise<void>
  onDirtyChange?: (isDirty: boolean) => void
}

const PromotionFormInner: FC<{
  storagePath: string
  enabledPlacements?: Partial<Record<PromotionPlacement, boolean>>
  metadata?: Record<string, string>
  onDelete?: () => void | Promise<void>
  onDirtyChange?: (isDirty: boolean) => void
  disabled?: boolean
  isPending: boolean
  promotionType?: PromotionType
}> = ({ storagePath, enabledPlacements, metadata, onDelete, onDirtyChange, disabled, isPending, promotionType }) => {
  const intl = useIntl()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const { watch, formState } = useFormContext()
  const { dirtyFields } = formState

  // Watch all form values to trigger re-renders
  const watchedValues = watch()

  // Use dirtyFields length to determine actual dirty state (avoids React Hook Form v7 isDirty bug)
  useEffect(() => {
    const hasActualChanges = Object.keys(dirtyFields).length > 0
    onDirtyChange?.(hasActualChanges)
  }, [dirtyFields, onDirtyChange, watchedValues])

  const handleConfirmDelete = useCallback(async () => {
    await onDelete?.()
    setDeleteDialogOpen(false)
  }, [onDelete])

  const placements = useMemo(
    () =>
      enabledPlacements
        ? PromotionPlacementZod.options.filter((placement) => enabledPlacements[placement])
        : PromotionPlacementZod.options,
    [enabledPlacements]
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-2">
        {onDelete && (
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="critical" size="sm" loading={isPending} disabled={disabled ?? isPending}>
                <Icons.trash className="size-3" fill="white" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{intl.formatMessage({ id: 'promotions.form.deleteDialog.title' })}</DialogTitle>
                <DialogDescription>
                  {intl.formatMessage({ id: 'promotions.form.deleteDialog.description' })}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="default"
                  onClick={() => {
                    setDeleteDialogOpen(false)
                  }}>
                  {intl.formatMessage({ id: 'promotions.form.deleteDialog.cancel' })}
                </Button>
                <Button type="button" variant="critical" onClick={handleConfirmDelete} loading={isPending}>
                  {intl.formatMessage({ id: 'promotions.form.deleteDialog.confirm' })}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <Button size="sm" type="submit" disabled={disabled ?? isPending}>
          {intl.formatMessage({ id: 'promotions.form.actions.save' })}
        </Button>
      </div>
      <FormField<PromotionFormData, 'active'>
        name="active"
        render={({ field: { value, onChange, ...field } }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormControl>
                <Switch id="active" checked={value} onCheckedChange={onChange} {...field} />
              </FormControl>
              <FormLabel className="font-bold" htmlFor="active">
                {intl.formatMessage({ id: 'promotions.form.displayEnabled' })}
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField<PromotionFormData, 'partnerName'>
        name="partnerName"
        render={({ field }) => (
          <FormItem>
            <FormLabel htmlFor="partnerName" className="font-bold">
              {intl.formatMessage({ id: 'promotions.form.partnerName' })}
            </FormLabel>
            <FormControl>
              <Input className="rounded-md" id="partnerName" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField<PromotionFormData, 'title'>
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel htmlFor="title" className="font-bold">
              {intl.formatMessage({ id: 'promotions.form.promotionTitle' })}
            </FormLabel>
            <FormControl>
              <Input className="rounded-md" id="title" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField<PromotionFormData, 'targetUrl'>
        name="targetUrl"
        render={({ field }) => (
          <FormItem>
            <FormLabel htmlFor="targetUrl" className="font-bold">
              {intl.formatMessage({ id: 'promotions.form.targetUrl' })}
            </FormLabel>
            <FormControl>
              <Input className="rounded-md" id="targetUrl" {...field} />
            </FormControl>
            {promotionType === 'fallback' && (
              <p className="text-sm text-gray-500">{intl.formatMessage({ id: 'promotions.form.targetUrl.helper' })}</p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <PlacementsWithVisualsSection
        placements={placements}
        storagePath={storagePath}
        metadata={metadata}
        disabled={disabled}
        intl={intl}
      />
    </div>
  )
}

export const PromotionForm: FC<PromotionFormProps> = ({
  storagePath,
  enabledPlacements,
  metadata,
  onDelete,
  onDirtyChange,
  disabled,
  promotionType,
  ...props
}) => {
  const [isPending, startTransition] = useTransition()

  return (
    <PromotionFormProvider disabled={disabled ?? isPending} promotionType={promotionType} {...props}>
      <PromotionFormInner
        storagePath={storagePath}
        enabledPlacements={enabledPlacements}
        metadata={metadata}
        onDelete={
          onDelete
            ? () => {
                startTransition(async () => {
                  await onDelete()
                })
              }
            : undefined
        }
        onDirtyChange={onDirtyChange}
        disabled={disabled}
        isPending={isPending}
        promotionType={promotionType}
      />
    </PromotionFormProvider>
  )
}

// Helper function to get dimensions for each placement
const getPlacementDimensions = (placement: PromotionPlacement) => {
  switch (placement) {
    case 'hero':
      return { width: 720, height: 720, className: 'size-[200px]' }
    case 'email':
    case 'milestone':
    case 'volunteerProfile':
    case 'clientInfo':
    case 'volunteerInfo':
      return { width: 800, height: 200, className: 'h-[50px] w-[200px]' }
    default:
      return { width: 200, height: 200, className: 'h-[50px] w-[200px]' }
  }
}

// Helper function to get label for each placement
const getPlacementLabel = (placement: PromotionPlacement, intl: IntlShape) => {
  // Determine size key based on placement
  const sizeKey = placement === 'hero' ? '720x720' : '800x200'

  // Determine type key based on placement
  const typeKey = placement === 'milestone' ? 'logo' : 'bannerOrLogo'

  return {
    title: intl.formatMessage({ id: `promotions.form.placements.${placement}` }),
    subtitle: intl.formatMessage({ id: `promotions.form.visuals.type.${typeKey}` }),
    description: intl.formatMessage({ id: `promotions.form.visuals.size.${sizeKey}` }),
  }
}

interface PlacementsWithVisualsSectionProps {
  placements: PromotionPlacement[]
  storagePath: string
  metadata?: Record<string, string>
  disabled?: boolean
  intl: IntlShape
}

const PlacementsWithVisualsSection: FC<PlacementsWithVisualsSectionProps> = ({
  placements,
  storagePath,
  metadata,
  disabled,
  intl,
}) => {
  const { control } = useFormContext<PromotionFormData>()
  const checkedPlacements = useWatch({
    control,
    name: 'placements',
  })

  return (
    <div className="space-y-4 rounded-md border border-gray-200 p-4">
      <h3 className="text-lg font-normal text-gray-900">
        {intl.formatMessage({ id: 'promotions.form.placements.title' })}
      </h3>

      <div className="space-y-6">
        {placements.map((placement) => {
          const isChecked = checkedPlacements?.[placement]
          const dimensions = getPlacementDimensions(placement)
          const labels = getPlacementLabel(placement, intl)

          return (
            <div key={placement} className="space-y-3">
              <FormField<PromotionFormData, `placements.${PromotionPlacement}`>
                name={`placements.${placement}`}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormItem>
                    <div className="flex items-center gap-3">
                      <FormControl>
                        <Checkbox
                          size="sm"
                          id={`placements.${placement}`}
                          checked={value}
                          onCheckedChange={onChange}
                          {...field}
                        />
                      </FormControl>
                      <FormLabel htmlFor={`placements.${placement}`}>
                        {intl.formatMessage({ id: `promotions.form.placements.${placement}` })}
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isChecked && (
                <FormField<PromotionFormData, `creativeUrls.${PromotionPlacement}`>
                  name={`creativeUrls.${placement}`}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormItem className="ml-9 flex items-center gap-4">
                      <FormControl>
                        <FileUpload
                          value={value}
                          onChange={onChange}
                          {...field}
                          storagePath={storagePath}
                          metadata={{
                            cacheControl: 'public,max-age=604800,immutable',
                            customMetadata: {
                              placement,
                              ...metadata,
                            },
                          }}
                          disabled={disabled}
                          className={dimensions.className}
                          height={dimensions.height}
                          width={dimensions.width}
                        />
                      </FormControl>
                      <div>
                        <p className="text-sm text-gray-600">{labels.subtitle}</p>
                        <p className="text-sm text-gray-500">{labels.description}</p>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
